import { Link } from 'react-router-dom';
import type { SubjectRecord } from '@ni/shared';
import { useNetwork } from '../../api/hooks';
import { RESEARCH_ASIDE } from '../../lib/copy';
import { StatusWord } from '../ui/status-word';
import { RecordProps } from '../record/record-props';
import '../record/record-body.css';

/**
 * A record's ties as the aside counts them, off the Network tab's own read: how
 * many others it touches, and the people among them already on record. A person
 * node carries a status only when the org holds a subject record for them
 * (`NetworkNode.status`). Null until the read lands.
 */
export function useRecordTies(subjectId: string, options: { poll?: boolean } = {}) {
  const data = useNetwork(subjectId, options).data;
  if (!data) return null;
  const others = data.nodes.filter(
    (node) => node.entity_id !== data.subject_entity_id && !node.unresolved,
  );
  const onRecord = others.filter(
    (node) => node.kind === 'person' && node.status !== null && node.display_name !== null,
  );
  return { connections: others.length, onRecord };
}

/** The people a record touches who are already on record, each with their status. */
export function PeopleOnRecord({
  vertical,
  people,
}: {
  vertical: string;
  people: NonNullable<ReturnType<typeof useRecordTies>>['onRecord'];
}) {
  return (
    <div className="props-block">
      <h4 className="sc">{RESEARCH_ASIDE.peopleOnRecord}</h4>
      {people.length > 0 ? (
        <ul className="ties">
          {people.map((node) => (
            <li key={node.entity_id}>
              <Link className="person" to={`/people/${node.entity_id}`}>
                {node.display_name}
              </Link>
              <span className="r">
                <StatusWord vertical={vertical} status={node.status!} />
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="quiet">{RESEARCH_ASIDE.noPeople}</p>
      )}
    </div>
  );
}

/**
 * **The record's properties, beside the brief** (blend spec, the Research
 * tab's right column): status, identity, where the research stands, the notes
 * and connections counts, and the people this person is tied to who are
 * already on record.
 *
 * Everything here is read off data the page already has or the Network tab
 * already loads; nothing is computed into a score. The status is the marker
 * and the word — the control that changes it stays in the record's head.
 */
export function ResearchAside({
  record,
  subjectId,
  research,
}: {
  record: SubjectRecord;
  subjectId: string;
  /** Where the research stands: a word, and a quieter line under it. */
  research: { word: string; note?: string | null };
}) {
  const ties = useRecordTies(subjectId);
  const vertical = record.subject.vertical;

  return (
    <aside className="research-aside record-aside" aria-label="About this record">
      <div className="props-block">
        <RecordProps record={record}>
          <dt>{RESEARCH_ASIDE.research}</dt>
          <dd>
            {research.word}
            {research.note ? <span className="quiet">{research.note}</span> : null}
          </dd>
          <dt>{RESEARCH_ASIDE.notes}</dt>
          <dd className="num">{record.notes.length}</dd>
          {ties ? (
            <>
              <dt>{RESEARCH_ASIDE.connections}</dt>
              <dd className="num">{ties.connections}</dd>
            </>
          ) : null}
        </RecordProps>
      </div>
      {ties ? <PeopleOnRecord vertical={vertical} people={ties.onRecord} /> : null}
    </aside>
  );
}
